import { Box, Center, HStack, Input, Text, VStack } from "@chakra-ui/react";
import { ArrowLeft, Home, Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import { useColorMode } from "~/components/ui/color-mode";
import { iconSize } from "~/components/ui/icon-size";
import { SimpleSlider } from "~/components/ui/slider";
import { useReducedMotion } from "~/hooks/useReducedMotion";
import { useRouter } from "~/utils/compat/next-router";
import {
  type CanvasColors,
  createNotFoundRenderer,
  defaultGridParams,
  type GridParams,
  MAX_CANVAS_DPR,
} from "./notFoundCanvasRenderer";

/**
 * A token's colour as RGB channels, for the canvas, which composes its own
 * alpha per stop. Painting the value onto a one-pixel canvas resolves any
 * colour syntax the token file uses.
 */
function readTokenRgb(variable: string): [number, number, number] {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
  const probe = document.createElement("canvas");
  probe.width = 1;
  probe.height = 1;
  const ctx = probe.getContext("2d", { willReadFrequently: true });
  if (!ctx || !value) return [0, 0, 0];
  ctx.fillStyle = value;
  ctx.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data;
  return [r, g, b];
}

/** The 404 scene's colours, read from the current theme's tokens. */
function readCanvasColors(): CanvasColors {
  const info = readTokenRgb("--color-info");
  return {
    gridBaseColor: info,
    particleBaseColor: info,
    aberrationRed: readTokenRgb("--color-err"),
    aberrationBlue: info,
    vignetteBaseColor: readTokenRgb("--color-scrim"),
    alphaScale: 1,
  };
}

function ParamSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <HStack gap={2} width="100%">
      <Text textStyle="xs" color="fg.2" width="80px" flexShrink={0}>
        {label}
      </Text>
      <SimpleSlider
        size="sm"
        width="120px"
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(e: { value: number[] }) =>
          onChange(e.value[0] ?? value)
        }
      />
      <Input
        size="xs"
        width="60px"
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      />
    </HStack>
  );
}

export function NotFoundScene() {
  const router = useRouter();
  const { colorMode } = useColorMode();
  const isDevMode = process.env.NODE_ENV === "development";
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const redTextRef = useRef<HTMLDivElement>(null);
  const blueTextRef = useRef<HTMLDivElement>(null);
  const raf = useRef(0);
  const mouse = useRef({ x: 0, y: 0 });
  const smoothMouse = useRef({ x: 0, y: 0 });
  const rendererRef = useRef(createNotFoundRenderer());

  const [showControls, setShowControls] = useState(false);
  const [params, setParams] = useState<GridParams>(defaultGridParams);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const prefersReducedMotion = useReducedMotion();
  const isVisible = useRef(true);
  const isTabActive = useRef(true);

  const updateParam = <K extends keyof GridParams>(
    key: K,
    value: GridParams[K],
  ) => {
    setParams((p) => ({ ...p, [key]: value }));
  };

  // The canvas reads the theme's own colours. The colour-mode provider writes
  // `data-theme` in an effect that runs after this component's, so the read
  // waits a frame for the new theme's tokens to be in place.
  const [colors, setColors] = useState<CanvasColors>(readCanvasColors);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setColors(readCanvasColors()));
    return () => cancelAnimationFrame(frame);
  }, [colorMode]);

  const onMove = useCallback((e: MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mouse.current.x = Math.max(
      -0.5,
      Math.min(0.5, (e.clientX - r.left) / r.width - 0.5),
    );
    mouse.current.y = Math.max(
      -0.5,
      Math.min(0.5, (e.clientY - r.top) / r.height - 0.5),
    );
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    container.addEventListener("mousemove", onMove);

    const render = rendererRef.current;

    const sizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_CANVAS_DPR);
      const w = rect.width;
      const h = rect.height;

      const pixelWidth = Math.round(w * dpr);
      const pixelHeight = Math.round(h * dpr);

      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }

      return { w, h, dpr };
    };

    // Reduced motion: render a single static frame, no animation loop
    if (prefersReducedMotion) {
      const { w, h, dpr } = sizeCanvas();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      render({
        ctx,
        width: w,
        height: h,
        timestamp: 0,
        params: paramsRef.current,
        colors,
        smoothMouse: { x: 0, y: 0 },
      });
      return () => {
        container.removeEventListener("mousemove", onMove);
      };
    }

    // Pause when tab is hidden
    const onVisibilityChange = () => {
      isTabActive.current = !document.hidden;
      if (!document.hidden && raf.current === 0) {
        raf.current = requestAnimationFrame(loop);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Pause when scrolled out of view
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible.current = entry?.isIntersecting ?? true;
        if (entry?.isIntersecting && raf.current === 0) {
          raf.current = requestAnimationFrame(loop);
        }
      },
      { threshold: 0 },
    );
    observer.observe(container);

    const loop = (timestamp: number) => {
      raf.current = 0;

      if (!isVisible.current || !isTabActive.current) return;

      const { w, h, dpr } = sizeCanvas();

      smoothMouse.current.x += (mouse.current.x - smoothMouse.current.x) * 0.06;
      smoothMouse.current.y += (mouse.current.y - smoothMouse.current.y) * 0.06;

      const textDx = smoothMouse.current.x * 3;
      const textDy = smoothMouse.current.y * 1.5;
      if (redTextRef.current) {
        redTextRef.current.style.transform = `translate(${-2 - textDx}px, ${-1 - textDy}px)`;
      }
      if (blueTextRef.current) {
        blueTextRef.current.style.transform = `translate(${2 + textDx}px, ${1 + textDy}px)`;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      render({
        ctx,
        width: w,
        height: h,
        timestamp,
        params: paramsRef.current,
        colors,
        smoothMouse: smoothMouse.current,
      });

      raf.current = requestAnimationFrame(loop);
    };

    raf.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf.current);
      raf.current = 0;
      container.removeEventListener("mousemove", onMove);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      observer.disconnect();
    };
  }, [onMove, colors, prefersReducedMotion]);

  return (
    <Center
      ref={containerRef}
      width="100%"
      height="100dvh"
      minHeight="400px"
      overflow="hidden"
      position="relative"
      css={{
        "@keyframes glitch-1": {
          "0%, 100%": { clipPath: "inset(0 0 96% 0)" },
          "20%": { clipPath: "inset(20% 0 60% 0)" },
          "40%": { clipPath: "inset(60% 0 10% 0)" },
          "60%": { clipPath: "inset(40% 0 30% 0)" },
          "80%": { clipPath: "inset(80% 0 5% 0)" },
        },
        "@keyframes glitch-2": {
          "0%, 100%": { clipPath: "inset(95% 0 0 0)" },
          "25%": { clipPath: "inset(10% 0 70% 0)" },
          "50%": { clipPath: "inset(50% 0 20% 0)" },
          "75%": { clipPath: "inset(30% 0 50% 0)" },
        },
        "@keyframes drift": {
          "0%, 100%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-6px)" },
        },
        "@keyframes text-glitch": {
          "0%, 92%, 100%": { transform: "none", opacity: 1 },
          "93%": { transform: "translateX(-2px) skewX(-1deg)", opacity: 0.8 },
          "94%": { transform: "translateX(3px) skewX(1deg)", opacity: 0.9 },
          "95%": { transform: "translateX(-1px)", opacity: 0.7 },
          "96%": { transform: "none", opacity: 1 },
        },
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
        }}
      />

      {isDevMode && (
        <Box
          position="absolute"
          top={2}
          right={2}
          zIndex="var(--z-sticky)"
          background="surface"
          borderRadius="md"
          padding={showControls ? 3 : 1}
          boxShadow="lg"
          maxHeight="90vh"
          overflowY="auto"
        >
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setShowControls(!showControls)}
            marginBottom={showControls ? 2 : 0}
          >
            <Settings style={iconSize.sm} />
            {showControls ? "Hide" : ""}
          </Button>

          {showControls && (
            <VStack gap={2} align="stretch">
              <ParamSlider
                label="Rotation"
                value={params.rotation}
                min={0}
                max={360}
                step={1}
                onChange={(v) => updateParam("rotation", v)}
              />
              <ParamSlider
                label="Z Offset"
                value={params.zOffset}
                min={100}
                max={15000}
                step={100}
                onChange={(v) => updateParam("zOffset", v)}
              />
              <ParamSlider
                label="FOV Scale"
                value={params.fovScale}
                min={0.1}
                max={3}
                step={0.01}
                onChange={(v) => updateParam("fovScale", v)}
              />
              <ParamSlider
                label="Camera Y"
                value={params.cameraY}
                min={0}
                max={5000}
                step={10}
                onChange={(v) => updateParam("cameraY", v)}
              />
              <ParamSlider
                label="Pitch"
                value={params.pitch}
                min={-90}
                max={90}
                step={1}
                onChange={(v) => updateParam("pitch", v)}
              />
              <ParamSlider
                label="Aberration"
                value={params.aberration}
                min={0}
                max={30}
                step={0.5}
                onChange={(v) => updateParam("aberration", v)}
              />
              <ParamSlider
                label="Grid size"
                value={params.gridExtent}
                min={500}
                max={10000}
                step={100}
                onChange={(v) => updateParam("gridExtent", v)}
              />
              <ParamSlider
                label="Grid step"
                value={params.gridStep}
                min={20}
                max={500}
                step={10}
                onChange={(v) => updateParam("gridStep", v)}
              />
              <Button
                size="xs"
                variant="outline"
                onClick={() => setParams(defaultGridParams)}
              >
                Reset
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  console.log("Grid params:", params);
                  void navigator.clipboard
                    .writeText(JSON.stringify(params, null, 2))
                    .catch((error: unknown) => {
                      console.warn("Copy failed:", error);
                    });
                }}
              >
                Copy params
              </Button>
            </VStack>
          )}
        </Box>
      )}

      <VStack gap={6} zIndex="var(--z-canvas-overlay)">
        <Box position="relative" userSelect="none">
          <Text
            fontWeight="semibold"
            fontSize="clamp(7rem, 18vw, 12rem)"
            lineHeight={1}
            letterSpacing="-0.04em"
            color="fg"
            opacity={0.12}
          >
            404
          </Text>

          <Text
            ref={redTextRef}
            aria-hidden
            position="absolute"
            inset={0}
            fontWeight="semibold"
            fontSize="clamp(7rem, 18vw, 12rem)"
            lineHeight={1}
            letterSpacing="-0.04em"
            color="err/70"
            animation={
              prefersReducedMotion ? "none" : "glitch-1 3s steps(1) infinite"
            }
            willChange="transform"
            style={{ transform: "translate(-2px, -1px)" }}
          >
            404
          </Text>
          <Text
            ref={blueTextRef}
            aria-hidden
            position="absolute"
            inset={0}
            fontWeight="semibold"
            fontSize="clamp(7rem, 18vw, 12rem)"
            lineHeight={1}
            letterSpacing="-0.04em"
            color="info/70"
            animation={
              prefersReducedMotion ? "none" : "glitch-2 2.5s steps(1) infinite"
            }
            willChange="transform"
            style={{ transform: "translate(2px, 1px)" }}
          >
            404
          </Text>
        </Box>

        <VStack
          gap={2}
          animation={
            prefersReducedMotion
              ? "none"
              : "drift 4s ease-in-out infinite, text-glitch 6s steps(1) infinite"
          }
        >
          <Text
            textStyle="lg"
            color="fg"
            fontWeight="normal"
            textAlign="center"
            css={{ textShadow: "0 1px 12px var(--color-bg)" }}
          >
            You've wandered out of the simulation
          </Text>
          <Text
            textStyle="sm"
            color="fg.2"
            textAlign="center"
            css={{ textShadow: "0 1px 12px var(--color-bg)" }}
          >
            This page doesn't exist or has been moved.
          </Text>
        </VStack>

        <HStack gap={3} marginTop={2}>
          <Button size="sm" variant="solid" onClick={() => router.back()}>
            <ArrowLeft style={iconSize.sm} />
            Go back
          </Button>
          <Button
            size="sm"
            variant="ghost"
            color="fg.2"
            onClick={() => void router.push("/")}
          >
            <Home style={iconSize.sm} />
            Home
          </Button>
        </HStack>
      </VStack>
    </Center>
  );
}
